import { type FeatureStatus, useMapStore } from "../store";
import { useId } from 'react';

function FilterMenu() {
    const headingId = useId();
    return (
        <div
            className="p-8 flex justify-center"
            id="map_filter"
            aria-labelledby="map_filter_button"
            role="dialog"
            aria-modal="true"
        >
            <div>
                <h2
                    id={headingId}
                    className="h6 text-grey-900 mb-4"
                >
                    Filter outages by:
                </h2>
                <div role="group" aria-labelledby={headingId}>
                    <Filter
                        name="Current Water Outage"
                        status="Active"
                        boxColorClass="bg-validation-red"
                        description="Shows active water outages in the area"
                    />
                    <Filter
                        name="Recently Completed Works"
                        status="Completed"
                        boxColorClass="bg-validation-green"
                        description="Shows recently completed maintenance work"
                    />
                    <Filter
                        name="Upcoming Water Outage"
                        status="Registered"
                        boxColorClass="bg-wattle-300"
                        description="Shows scheduled future water outages"
                    />
                    <Filter
                        name="Major Works"
                        status="Major"
                        boxColorClass="bg-validation-orange"
                        description="Shows major infrastructure work"
                    />
                </div>
            </div>

            {/* Add close button */}
            <button
                className="absolute top-4 right-4 p-2"
                onClick={() => useMapStore.getState().setDisplay(null)}
                aria-label="Close filters"
            >
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                >
                    <path d="M18 6L6 18M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
}

type FilterProps = {
    name: string;
    status: FeatureStatus;
    boxColorClass: string;
    description: string;
}

function Filter({ name, status, boxColorClass, description }: FilterProps) {
    const { filterStatus, setFilter } = useMapStore();
    const checkboxId = useId();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(status, e.target.checked);
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFilter(status, !filterStatus[status]);
        }
    }

    return (
        <div className="flex items-center justify-between py-2">
            <label className="flex items-center gap-2 label-secondary mb-0">
                <input
                    type="checkbox"
                    className="check-primary"
                    checked={filterStatus[status]}
                    name={status}
                    onChange={handleChange}
                    onKeyDown={handleKeyPress}
                    aria-describedby={`${checkboxId}-description`}
                />
                <span className="flex items-center cursor-pointer">
                    {name}
                </span>
                <span
                    id={`${checkboxId}-description`}
                    className="sr-only"
                >
                    {description}
                </span>
            </label>
            <div
                className={`ml-2 rounded-md h-4 w-8 ${boxColorClass}`}
                role="presentation"
                aria-hidden="true"
            />
        </div>
    );
}

export default FilterMenu;